import { ProfileResponseModel } from '../../../../types/Profile';

export type MissingProfileField = {
  key: string;
  label: string;
  benefit: string;
};

export type ProfileCompletionResult = {
  totalFields: number;
  completedFields: number;
  completionPercentage: number;
  missingFields: MissingProfileField[];
  isComplete: boolean;
};

type CompletionFieldConfig = MissingProfileField & {
  isComplete: (profile: ProfileResponseModel) => boolean;
};

const isNonEmptyText = (value?: string | null) =>
  typeof value === 'string' && value.trim().length > 0;

const professionalFields: CompletionFieldConfig[] = [
  {
    key: 'profilePicture',
    label: 'Profile Photo',
    benefit: 'Profiles with a photo attract significantly more client attention',
    isComplete: (profile) => isNonEmptyText(profile.profilePicture),
  },
  {
    key: 'headerBio',
    label: 'Header Bio',
    benefit:
      'A compelling bio lets clients understand your expertise at a glance',
    isComplete: (profile) => isNonEmptyText(profile.headerBio),
  },
  {
    key: 'city',
    label: 'Location',
    benefit: 'Local clients are more likely to discover and book you',
    isComplete: (profile) => isNonEmptyText(profile.city),
  },
  {
    key: 'categoryTags',
    label: 'Categories',
    benefit: 'Appear in the right searches and attract your ideal clients',
    isComplete: (profile) => profile.userCategories.length > 0,
  },
  {
    key: 'searchTermTags',
    label: 'Search Terms',
    benefit: 'Get discovered by people looking for exactly what you offer',
    isComplete: (profile) => profile.userSearchTerms.length > 0,
  },
];

const regularFields: CompletionFieldConfig[] = [
  {
    key: 'profilePicture',
    label: 'Profile Photo',
    benefit: 'A photo builds recognition and makes your profile feel personal',
    isComplete: (profile) => isNonEmptyText(profile.profilePicture),
  },
  {
    key: 'about',
    label: 'About',
    benefit: 'Letting people know a little about you goes a long way',
    isComplete: (profile) => isNonEmptyText(profile.about),
  },
];

export const getProfileCompletion = (
  profile: ProfileResponseModel
): ProfileCompletionResult => {
  const fieldsToCheck = profile.isProfessional ? professionalFields : regularFields;

  const missingFields = fieldsToCheck
    .filter((field) => !field.isComplete(profile))
    .map(({ key, label, benefit }) => ({ key, label, benefit }));

  const totalFields = fieldsToCheck.length;
  const completedFields = totalFields - missingFields.length;
  const completionPercentage = Math.round((completedFields / totalFields) * 100);

  return {
    totalFields,
    completedFields,
    completionPercentage,
    missingFields,
    isComplete: missingFields.length === 0,
  };
};